/**
 * Keep a Mapbox canvas the size of the box it sits in.
 *
 * Mapbox measures its container once, at load. Anything that changes the
 * container afterwards — a side panel collapsing, a dock being dragged wider,
 * a dialog opening — leaves the canvas at its old size, which reads as the map
 * having stopped at an invisible edge with dead space beside it.
 *
 * The resize is debounced rather than immediate, which matters more than it
 * looks. A `ResizeObserver` fires on every frame of a CSS transition, and
 * `map.resize()` reallocates the WebGL backing store each time; sixty of those
 * in three hundred milliseconds produces a white flash that reads as the map
 * reloading. Waiting for the container to stop changing collapses the storm
 * into one resize, which is the same conclusion `PathfinderMapGL` reached.
 */
import { RefObject, useEffect } from 'react';

/** Long enough to outlast a CSS transition, short enough not to be noticed. */
const SETTLE_MS = 120;

export function useMapAutoResize(
  containerRef: RefObject<HTMLElement | null>,
  getMap: () => { resize: () => void } | null | undefined,
  /**
   * Flip to true once the container exists. A map inside a dialog is not
   * mounted while the dialog is shut, so an observer attached on first render
   * would find nothing to watch and never look again.
   */
  active: boolean = true,
): void {
  useEffect(() => {
    const container = active ? containerRef.current : null;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const settle = (delay = SETTLE_MS) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        try {
          getMap()?.resize();
        } catch {
          // The map can be torn down mid-transition, on navigation or on a
          // dialog closing. A resize that arrives after that is not a problem.
        }
      }, delay);
    };

    const observer = new ResizeObserver(() => settle());
    observer.observe(container);
    // Once on mount as well: a map created inside a dialog or a hidden tab was
    // measured against a container that had not been laid out yet.
    settle(0);

    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [containerRef, getMap, active]);
}

export default useMapAutoResize;
